import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Mail, Lock, User, Phone, Building, Eye, EyeOff } from "lucide-react";

const AuthCard = ({ type }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { signUp, signIn } = useAuth();
  const navigate = useNavigate();

  const isLogin = type === "login";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const email = e.target.email.value;
    const password = e.target.password.value;

    try {
      if (isLogin) {
        // Sign In
        const { data, error: authError } = await signIn(email, password);
        if (authError) throw new Error(authError);
        // Redirect to dashboard on successful login
        navigate('/dashboard');
      } else {
        // Sign Up
        const fullName = e.target.name.value;
        const phone = e.target.phone.value;
        const practice = e.target.practice.value;

        const { data, error: authError } = await signUp(email, password, {
          full_name: fullName,
          phone,
          practice,
          role: 'client',
        });
        if (authError) throw new Error(authError);
        // Redirect to dashboard on successful signup
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.message || "An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1 style={{ color: 'var(--color-gold-light)' }}>
            {isLogin ? "Welcome Back" : "Create Account"}
          </h1>
          <p>
            {isLogin
              ? "Sign in to your account to continue"
              : "Create your account to get started with RuaMedical"}
          </p>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {!isLogin && (
            <div className="form-group">
              <label htmlFor="name">Full Name</label>
              <div style={{ position: "relative" }}>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="form-control"
                  placeholder="Enter your full name"
                  style={{ paddingLeft: "2.5rem" }}
                  required
                />
                <User
                  size={18}
                  style={{
                    position: "absolute",
                    left: "0.75rem",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: 'var(--color-gray-500)',
                  }}
                />
              </div>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <div style={{ position: "relative" }}>
              <input
                type="email"
                id="email"
                name="email"
                className="form-control"
                placeholder="Enter your email"
                style={{ paddingLeft: "2.5rem" }}
                required
              />
              <Mail
                size={18}
                style={{
                  position: "absolute",
                  left: "0.75rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: 'var(--color-gray-500)',
                }}
              />
            </div>
          </div>

          {!isLogin && (
            <>
              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <div style={{ position: "relative" }}>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className="form-control"
                    placeholder="Enter your phone number"
                    style={{ paddingLeft: "2.5rem" }}
                  />
                  <Phone
                    size={18}
                    style={{
                      position: "absolute",
                      left: "0.75rem",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: 'var(--color-gray-500)',
                    }}
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="practice">Practice Name</label>
                <div style={{ position: "relative" }}>
                  <input
                    type="text"
                    id="practice"
                    name="practice"
                    className="form-control"
                    placeholder="Enter your practice name"
                    style={{ paddingLeft: "2.5rem" }}
                  />
                  <Building
                    size={18}
                    style={{
                      position: "absolute",
                      left: "0.75rem",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: 'var(--color-gray-500)',
                    }}
                  />
                </div>
              </div>
            </>
          )}

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                className="form-control"
                placeholder={isLogin ? "Enter your password" : "Create a password"}
                style={{ paddingLeft: "2.5rem", paddingRight: "2.5rem" }}
                required
              />
              <Lock
                size={18}
                style={{
                  position: "absolute",
                  left: "0.75rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: 'var(--color-gray-500)',
                }}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="btn-primary"
            style={{ width: "100%", marginTop: "1.5rem", marginBottom: "1rem" }}
            disabled={loading}
          >
            {loading
              ? "Please wait..."
              : isLogin
              ? "Sign In"
              : "Create Account"}
          </button>
        </form>

        <div className="auth-link">
          {isLogin ? "Don't have an account? " : "Already have an account? "}
          <Link to={isLogin ? "/signup" : "/login"}>
            {isLogin ? "Sign up" : "Sign in"}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AuthCard;
